/**
 * Encrypted thumbnails.
 *
 * The design shows a grid of media, which needs previews, and a preview that
 * left the browser in the clear would undo the encryption for exactly the files
 * people care most about. So thumbnails are generated here, encrypted under a
 * key derived from the node's own key, and stored as opaque bytes like
 * everything else.
 *
 * Derived rather than reusing the node key directly: a thumbnail is served on a
 * separate, more permissive cache path, and using a distinct key means a
 * mistake there cannot become a way to read the file itself.
 */

import { aesDecrypt, aesEncrypt, exportRaw, hkdfAesKey, toBase64, type Bytes } from './crypto/primitives';

/** Long edge, in CSS pixels. Large enough for the design's 116px media band on
 *  a 2× display, small enough that a folder of photos is not a download. */
const MAX_EDGE = 320;
const QUALITY = 0.72;

/** Bounded server-side too; kept in step so a rejection is never a surprise. */
export const MAX_THUMBNAIL_BYTES = 256 * 1024;

const thumbKey = async (nodeKey: CryptoKey): Promise<CryptoKey> =>
  hkdfAesKey(await exportRaw(nodeKey), 'mauvely.cloud.thumbnail.v1');

/** Types worth trying. Video would need a frame grab and a decoder; PDF would
 *  need a renderer. Both are backlog items rather than silent failures. */
export const canThumbnail = (mime: string | null): boolean =>
  !!mime && /^image\/(png|jpeg|webp|gif|avif)$/.test(mime);

/**
 * Draw a file down to a thumbnail and encrypt it.
 *
 * Returns null when the file is not an image or the browser cannot decode it —
 * a missing thumbnail is a cosmetic loss, never a failed upload.
 */
export async function makeThumbnail(file: Blob, nodeKey: CryptoKey): Promise<Uint8Array | null> {
  if (!canThumbnail(file.type)) return null;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return null;
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', QUALITY)
  );
  if (!blob || blob.size > MAX_THUMBNAIL_BYTES) return null;

  const plaintext = new Uint8Array(await blob.arrayBuffer());
  const payload = await aesEncrypt(await thumbKey(nodeKey), plaintext);
  plaintext.fill(0);

  // Stored as the same `<iv>.<ct>` text everything else uses, so one decrypt
  // path covers thumbnails too.
  return new TextEncoder().encode(payload);
}

export async function decryptThumbnail(encrypted: Uint8Array, nodeKey: CryptoKey): Promise<Blob> {
  const payload = new TextDecoder().decode(encrypted);
  const plain: Bytes = await aesDecrypt(await thumbKey(nodeKey), payload);
  return new Blob([plain], { type: 'image/webp' });
}

/** Debug aid: the thumbnail key's fingerprint, never the key. */
export const thumbKeyLabel = async (nodeKey: CryptoKey): Promise<string> =>
  toBase64(await exportRaw(nodeKey)).slice(0, 8);
